import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthStore } from '../store/auth.store';

/**
 * Attaches the right bearer token to outgoing requests:
 * - IOBase/Indaba URLs (io-base.com) → `iobaseBearer`
 * - everything else                  → Terega/CORTEX `bearer`
 *
 * The Terega/CORTEX access token only carries the Auth0 `sub`, so for our own API we
 * also forward the connected user's name/email (from the Auth0 profile) as
 * `X-User-*` headers. Values are percent-encoded so accented names stay ASCII-safe.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // External fire API does not allow Authorization in preflight.
  if (environment.fireBaseUrl && req.url.startsWith(environment.fireBaseUrl)) {
    return next(req);
  }

  const store = inject(AuthStore);
  const user = store.user();
  if (!user) {
    return next(req);
  }

  const isIobase = req.url.includes('io-base.com');
  const setHeaders: Record<string, string> = {};

  // TEMPORARY: the anonymous user holds no tokens, so an `Authorization` header would
  // only carry an empty bearer for the API's JWT handler to reject.
  if (!environment.authDisabled) {
    setHeaders['Authorization'] = `Bearer ${isIobase ? user.iobaseBearer : user.bearer}`;
  }

  if (!isIobase) {
    const fullName = user.fullName?.trim();
    if (fullName && !fullName.includes('undefined')) {
      setHeaders['X-User-Name'] = encodeURIComponent(fullName);
    }
    if (user.email) {
      setHeaders['X-User-Email'] = encodeURIComponent(user.email);
    }
  }

  return next(req.clone({ setHeaders }));
};
